import {
  buildCopilotCampaignLabels,
  buildDetailedAmbientComparisonRows,
  getCopilotAmbientProductivity,
  getCopilotCampaignIdentity,
  getCopilotCampaignSourceLabel,
} from './copilotModel'
import {getCropDefinition} from '../../../shared/utils/cropCatalog'

const asRecords = (value) =>
  (Array.isArray(value) ? value : []).filter(
    (record) => record && typeof record === 'object' && !Array.isArray(record)
  )

const textValue = (value) =>
  typeof value === 'string' || typeof value === 'number' ? String(value).trim() : ''

const finiteNumber = (value) => {
  if (typeof value !== 'number' && typeof value !== 'string') return null
  if (typeof value === 'string' && !value.trim()) return null
  const number = Number(value)
  return Number.isFinite(number) ? (number === 0 ? 0 : number) : null
}

const hasValue = (value) => value !== undefined && value !== null && value !== ''
const mergeMissing = (left, right) => {
  const merged = {...left}
  Object.entries(right).forEach(([key, value]) => {
    if (!hasValue(merged[key]) && hasValue(value)) merged[key] = value
  })
  return merged
}

const campaignIdentity = (campaign) => {
  const identity = getCopilotCampaignIdentity(campaign)
  if (identity) return identity
  const metadata = ['campaign', 'crop', 'reportName', 'createdAt'].map((key) =>
    textValue(campaign?.[key])
  )
  return metadata.some(Boolean) ? `metadata:${JSON.stringify(metadata)}` : ''
}

const uniqueCampaignRecords = (records) => {
  const campaigns = new Map()
  asRecords(records).forEach((campaign) => {
    const identity = campaignIdentity(campaign)
    if (!identity) return
    campaigns.set(identity, mergeMissing(campaigns.get(identity) || {}, campaign))
  })
  return campaigns
}

const campaignMetadata = (campaign) =>
  [
    'campaignKey',
    'campaignGroupKey',
    'sourceKey',
    'campaign',
    'crop',
    'reportId',
    'reportName',
    'layerId',
    'createdAt',
  ].reduce((metadata, key) => {
    if (hasValue(campaign?.[key])) metadata[key] = campaign[key]
    return metadata
  }, {})

const CROP_KEYS = {
  corn: 'corn',
  maize: 'corn',
  maíz: 'corn',
  maiz: 'corn',
  soybean: 'soybean',
  soy: 'soybean',
  soja: 'soybean',
  wheat: 'wheat',
  trigo: 'wheat',
  peanut: 'peanut',
  maní: 'peanut',
  mani: 'peanut',
}

const COLUMN_LABELS = {
  es: {
    ambient: 'Ambiente',
    soil: 'Suelo dominante',
    soil_unit: 'Unidad de suelo',
    campaign: 'Campaña',
    crop: 'Cultivo',
    report: 'Reporte',
    surface: 'Superficie',
    covered_surface: 'Superficie con rendimiento',
    elevation: 'Altura promedio',
    yield: 'Rendimiento',
    margin: 'Margen bruto',
  },
  en: {
    ambient: 'Management zone',
    soil: 'Dominant soil',
    soil_unit: 'Soil unit',
    campaign: 'Campaign',
    crop: 'Crop',
    report: 'Report',
    surface: 'Surface',
    covered_surface: 'Surface with yield data',
    elevation: 'Mean elevation',
    yield: 'Yield',
    margin: 'Gross margin',
  },
}

const TABLE_CONTENT_WIDTH = 515
const COLUMN_WIDTH_WEIGHTS = {
  ambient: 80,
  soil: 100,
  soil_unit: 100,
  campaign: 70,
  crop: 55,
  report: 120,
  surface: 55,
  covered_surface: 65,
  elevation: 55,
  yield: 55,
  margin: 60,
}

const CELL_FIELDS = [
  'ambientClass',
  'soilUnit',
  'soilProductivityIndex',
  'surfaceHa',
  'coveredAreaHa',
  'meanElevationM',
  'economicYieldTonsHa',
  'grossMarginUsdHa',
]

const uniqueCells = (campaigns) => {
  const seen = new Set()
  return campaigns
    .flatMap((campaign) => asRecords(campaign?.cells))
    .filter((cell) => {
      const identity = JSON.stringify(CELL_FIELDS.map((key) => cell[key] ?? null))
      if (seen.has(identity)) return false
      seen.add(identity)
      return true
    })
}

// All rows come from the saved snapshot. No selector state or DOM tables enter this model.
export const buildCopilotPdfTables = (analysis, t, locale = 'es') => {
  const integrated =
    analysis?.crossLayerAnalysis?.integratedAnalysis || analysis?.integratedAnalysis
  if (!integrated || typeof integrated !== 'object') return []

  const language = String(locale || 'es')
    .toLowerCase()
    .startsWith('en')
    ? 'en'
    : 'es'
  let numberLocale = language
  try {
    numberLocale = new Intl.NumberFormat(locale || language).resolvedOptions().locale
  } catch (_) {
    // Saved data remain exportable even if an old snapshot has an invalid language tag.
  }
  const translated = (key, es, en, values = {}) => {
    const fallback = language === 'en' ? en : es
    const value = typeof t === 'function' ? t(key, {...values, defaultValue: fallback}) : ''
    const template = typeof value === 'string' && value && value !== key ? value : fallback
    return Object.entries(values).reduce(
      (result, [name, replacement]) => result.replaceAll(`{{${name}}}`, String(replacement)),
      template
    )
  }
  const metric = (value, unit, digits = 2) => {
    const number = finiteNumber(value)
    return number === null
      ? '-'
      : `${new Intl.NumberFormat(numberLocale, {
          minimumFractionDigits: digits,
          maximumFractionDigits: digits,
        }).format(number)} ${unit}`
  }
  const number = (value, digits) => {
    const numeric = finiteNumber(value)
    return numeric === null
      ? ''
      : new Intl.NumberFormat(numberLocale, {
          minimumFractionDigits: digits,
          maximumFractionDigits: digits,
        }).format(numeric)
  }
  const crop = (value) => {
    const raw = textValue(value)
    const definition = getCropDefinition(raw)
    const key = CROP_KEYS[raw.toLowerCase()] || definition?.economicId
    const labels = {
      corn: ['Maíz', 'Maize'],
      soybean: ['Soja', 'Soybean'],
      wheat: ['Trigo', 'Wheat'],
      peanut: ['Maní', 'Peanut'],
    }
    const localized = labels[key] || (definition ? [definition.labels.es, definition.labels.en] : null)
    return key && localized ? translated(`integrated_analysis.crops.${key}`, ...localized) : raw || '-'
  }
  const soil = (value, productivity, share) => {
    const details = [textValue(value)]
    const index = number(productivity, 0)
    if (index)
      details.push(
        translated('integrated_analysis.soil_productivity_index', 'IP {{value}}', 'PI {{value}}', {
          value: index,
        })
      )
    const percent = number(share, 1)
    if (percent)
      details.push(
        translated(
          'integrated_analysis.soil_share',
          '{{value}}% del ambiente',
          '{{value}}% of the zone',
          {value: percent}
        )
      )
    return details.filter(Boolean).join(' · ') || '-'
  }
  const column = (key, read, required = false) => ({
    header: translated(
      `integrated_analysis.columns.${key}`,
      COLUMN_LABELS.es[key],
      COLUMN_LABELS.en[key]
    ),
    read,
    required,
    widthWeight: COLUMN_WIDTH_WEIGHTS[key],
  })
  const table = (context, records, columns, emptyMessage = '') => {
    // Remove columns with no observations; zero and negative margins remain real values.
    const visible = columns.filter(
      (entry) => entry.required || records.some((record) => entry.read(record) !== '-')
    )
    const totalWeight = visible.reduce((sum, entry) => sum + entry.widthWeight, 0)
    return {
      ...context,
      headers: records.length ? visible.map((entry) => entry.header) : [],
      rows: records.map((record) => visible.map((entry) => entry.read(record))),
      columnWidths: records.length
        ? visible.map((entry) => (TABLE_CONTENT_WIDTH * entry.widthWeight) / totalWeight)
        : [],
      emptyMessage: records.length ? '' : emptyMessage,
    }
  }

  const ambientRows = asRecords(integrated.ambientRows)
  const ambientValues = [...new Set(ambientRows.map((row) => row?.ambientClass))]
  const descriptors = ambientRows
    .map((row) => ({
      row,
      ...getCopilotAmbientProductivity(row?.ambientClass, ambientValues, language),
    }))
    .sort((left, right) => {
      if (left.rank !== null && right.rank !== null) return right.rank - left.rank
      return String(right.code).localeCompare(String(left.code), numberLocale, {numeric: true})
    })
  const tripleCampaigns = asRecords(integrated.tripleCross?.campaigns)
  const campaigns = uniqueCampaignRecords([
    ...asRecords(integrated.campaigns),
    ...ambientRows.flatMap((row) => asRecords(row?.campaigns)),
    ...tripleCampaigns,
  ])
  const metadata = new Map(
    [...campaigns].map(([identity, record]) => [identity, campaignMetadata(record)])
  )
  const labelRecords = [...metadata].map(([identity, record]) => ({
    ...record,
    campaignKey: identity,
  }))
  const campaignLabels = buildCopilotCampaignLabels(labelRecords, {
    locale: numberLocale,
    formatCrop: crop,
  })
  const label = (identity) => {
    const value = campaignLabels[identity]
    return value && value !== '-'
      ? value
      : translated(
          'pdf.tables.unknown_campaign',
          'Campaña sin identificar',
          'Unidentified campaign'
        )
  }
  const campaignNotice = (identity) =>
    translated(
      'integrated_analysis.comparison_notice',
      'Resultados de {{campaign}}.',
      'Results for {{campaign}}.',
      {campaign: label(identity)}
    )
  const comparisonTitle = translated(
    'integrated_analysis.comparison_title',
    'Comparación por ambiente',
    'Management zone comparison'
  )
  const emptyComparison = translated(
    'pdf.tables.no_comparison',
    'No hay datos para comparar los ambientes.',
    'There is no management-zone comparison data.'
  )
  const comparisonColumns = [
    column('ambient', ({descriptor}) => descriptor.label, true),
    column('soil', ({descriptor}) =>
      soil(
        descriptor.row?.dominantSoilUnit,
        descriptor.row?.dominantSoilProductivityIndex,
        descriptor.row?.dominantSoilSharePercent
      )
    ),
    column('surface', ({descriptor}) => metric(descriptor.row?.surfaceHa, 'ha')),
    column('covered_surface', ({performance}) => metric(performance?.coveredAreaHa, 'ha')),
    column('elevation', ({descriptor}) => metric(descriptor.row?.terrain?.meanElevationM, 'm', 1)),
    column('yield', ({performance}) => metric(performance?.economicYieldTonsHa, 't/ha')),
    column('margin', ({performance}) => metric(performance?.grossMarginUsdHa, 'USD/ha')),
  ]
  const blocks = []
  if (!campaigns.size) {
    blocks.push(
      table(
        {
          kind: 'comparison',
          title: comparisonTitle,
          subtitle: '',
        },
        descriptors.map((descriptor) => ({descriptor})),
        comparisonColumns,
        emptyComparison
      )
    )
    blocks.push({
      kind: 'notice',
      title: '',
      subtitle: '',
      headers: [],
      rows: [],
      columnWidths: [],
      emptyMessage: translated(
        'pdf.tables.no_campaigns',
        'El análisis guardado no incluye resultados de rendimiento o margen por campaña.',
        'The saved analysis does not include yield or margin results by campaign.'
      ),
    })
    return blocks
  }

  descriptors.forEach((descriptor) => {
    const records = [...uniqueCampaignRecords(descriptor.row?.campaigns)].map(
      ([identity, performance]) => ({
        descriptor,
        performance,
        campaign: mergeMissing(performance, metadata.get(identity) || {}),
        identity,
      })
    )
    blocks.push(
      table(
        {
          kind: 'history',
          ambientClass: textValue(descriptor.row?.ambientClass),
          ambientLabel: descriptor.label,
          title: `${translated(
            'integrated_analysis.campaign_history_title',
            'Rendimiento y margen del ambiente entre campañas',
            'Management zone yield and margin across campaigns'
          )} · ${descriptor.label}`,
          subtitle: records.length
            ? translated(
                'pdf.tables.history_notice',
                'Incluye todas las campañas disponibles para este ambiente en el análisis guardado.',
                'Includes every available campaign for this management zone in the saved analysis.'
              )
            : '',
        },
        records,
        [
          column(
            'campaign',
            ({campaign, identity}) => {
              const year = textValue(campaign?.campaign)
              return year && year !== '-' ? year : label(identity)
            },
            true
          ),
          column('crop', ({campaign}) => crop(campaign?.crop)),
          column(
            'report',
            ({campaign}) => getCopilotCampaignSourceLabel(campaign, numberLocale) || '-'
          ),
          ...comparisonColumns.slice(2),
        ],
        translated(
          'pdf.tables.no_history',
          'No hay resultados por campaña para este ambiente.',
          'There are no campaign results for this management zone.'
        )
      )
    )
  })

  campaigns.forEach((_, identity) => {
    const records = descriptors.map((descriptor) => ({
      descriptor,
      performance: uniqueCampaignRecords(descriptor.row?.campaigns).get(identity),
    }))
    blocks.push(
      table(
        {
          kind: 'comparison',
          campaignKey: identity,
          campaignLabel: label(identity),
          title: comparisonTitle,
          subtitle: campaignNotice(identity),
        },
        records,
        comparisonColumns,
        emptyComparison
      )
    )

    const cells = buildDetailedAmbientComparisonRows(
      uniqueCells(tripleCampaigns.filter((campaign) => campaignIdentity(campaign) === identity)),
      ambientValues,
      numberLocale
    )
    blocks.push(
      table(
        {
          kind: 'detail',
          campaignKey: identity,
          campaignLabel: label(identity),
          title: translated(
            'integrated_analysis.triple_title',
            'Comparación por ambiente detallada',
            'Detailed management zone comparison'
          ),
          subtitle: cells.length
            ? `${campaignNotice(identity)} ${translated(
                'pdf.tables.detail_notice',
                'Cada fila corresponde a una unidad de suelo y un ambiente. La superficie es el área total de esa intersección.',
                'Each row belongs to one soil unit and one management zone. Surface is the total area of that intersection.'
              )}`
            : campaignNotice(identity),
        },
        cells,
        [
          column('ambient', (cell) => cell.descriptor.label, true),
          column('soil_unit', (cell) => soil(cell?.soilUnit, cell?.soilProductivityIndex), true),
          column('surface', (cell) => metric(cell?.surfaceHa, 'ha')),
          column('covered_surface', (cell) => metric(cell?.coveredAreaHa, 'ha')),
          column('elevation', (cell) => metric(cell?.meanElevationM, 'm', 1)),
          column('yield', (cell) => metric(cell?.economicYieldTonsHa, 't/ha')),
          column('margin', (cell) => metric(cell?.grossMarginUsdHa, 'USD/ha')),
        ],
        translated(
          'pdf.tables.no_detail',
          'No hay datos del cruce suelo y ambiente para esta campaña.',
          'There is no soil-zone intersection data for this campaign.'
        )
      )
    )
  })
  return blocks
}
